import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useEffect } from 'react';

export default function PlaybookExecutiveSummary() {
  useAnalytics('Constitutional AI Implementation Playbook - Executive Summary');

  useEffect(() => {
    // Set document title
    document.title = 'The Constitutional AI Implementation Playbook: Executive Summary | Constitutional AI Policy Institute';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Executive Summary: Operationalizing the Third Way - From Black Box Liability to Glass Box Trust. A 5-phase engineering framework to embed safety norms directly into AI models, transforming AI from opaque black boxes into auditable glass boxes.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Executive Summary: Operationalizing the Third Way - From Black Box Liability to Glass Box Trust. A 5-phase engineering framework to embed safety norms directly into AI models, transforming AI from opaque black boxes into auditable glass boxes.';
      document.head.appendChild(meta);
    }

    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Constitutional AI playbook, AI implementation framework, glass box AI, AI safety engineering, NIST AI RMF, Constitutional Error Rate, RLAIF, AI governance framework, auditable AI');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Constitutional AI playbook, AI implementation framework, glass box AI, AI safety engineering, NIST AI RMF, Constitutional Error Rate, RLAIF, AI governance framework, auditable AI';
      document.head.appendChild(meta);
    }

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://algorithmicconsistency.org/playbook-executive-summary');

    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'The Constitutional AI Implementation Playbook: Executive Summary');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'The Constitutional AI Implementation Playbook: Executive Summary';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Operationalizing the Third Way: From Black Box Liability to Glass Box Trust. A 5-phase engineering framework for auditable, trustworthy AI systems.');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Operationalizing the Third Way: From Black Box Liability to Glass Box Trust. A 5-phase engineering framework for auditable, trustworthy AI systems.';
      document.head.appendChild(meta);
    }

    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'article');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      meta.content = 'article';
      document.head.appendChild(meta);
    }

    // Add Article schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The Constitutional AI Implementation Playbook: Executive Summary",
      "description": "Operationalizing the Third Way: From Black Box Liability to Glass Box Trust. A 5-phase engineering framework to embed safety norms directly into AI models.",
      "author": {
        "@type": "Organization",
        "name": "Algorithmic Consistency Initiative",
        "url": "https://AlgorithmicConsistency.org"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Algorithmic Consistency Initiative, LLC",
        "url": "https://AlgorithmicConsistency.org"
      },
      "datePublished": "2025-01-15",
      "dateModified": "2025-01-15",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://algorithmicconsistency.org/playbook-executive-summary"
      },
      "keywords": [
        "Constitutional AI",
        "AI Implementation",
        "Glass Box AI",
        "AI Safety Engineering",
        "NIST AI RMF",
        "RLAIF"
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        
        <article className="container mx-auto px-6 lg:px-12 py-20 max-w-5xl">
          {/* Header */}
          <header className="mb-16 text-center">
            <div className="inline-block bg-red-800 text-white px-4 py-2 text-sm font-bold mb-6 whitespace-nowrap">
              EXECUTIVE SUMMARY
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" style={{ fontFamily: "'Inter', sans-serif" }}>
              THE CONSTITUTIONAL AI IMPLEMENTATION PLAYBOOK
            </h1>
            <p className="text-2xl text-red-800 font-semibold mb-4">
              Operationalizing the "Third Way": From Black Box Liability to Glass Box Trust
            </p>
            <div className="mt-8 text-slate-700">
              <p className="text-lg font-semibold">Prepared By: The Algorithmic Consistency Initiative (ACI)</p>
              <p className="text-lg italic">Mission: Engineering Civil Rights into the Digital Age</p>
            </div>
          </header>

          {/* The Paradigm Shift */}
          <section className="mb-16 bg-slate-50 p-8 rounded-lg border-l-4 border-red-800">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">THE PARADIGM SHIFT</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Current AI safety relies on "Red Teaming" and human feedback (RLHF), which creates opaque models that mimic human bias. <strong>This approach constitutes a foreseeable design defect.</strong>
            </p>
            <div className="bg-red-50 p-6 rounded-lg mt-6">
              <p className="text-lg text-slate-900 font-semibold mb-2">The Solution:</p>
              <p className="text-lg text-slate-700 leading-relaxed">
                A <strong>5-Phase Engineering Framework</strong> to embed safety norms directly into the model's objective function, transforming AI from a "Black Box" into an auditable "Glass Box".
              </p>
            </div>
          </section>

          {/* The 5-Phase Framework */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">THE 5-PHASE IMPLEMENTATION FRAMEWORK</h2>

            {/* Phase 1 */}
            <div className="mb-10 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-16 h-16 bg-red-800 text-white flex items-center justify-center rounded-lg">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">PHASE 1: THE DIAGNOSIS</h3>
                  <p className="text-lg text-red-800 font-semibold">(Governance &amp; Scope)</p>
                </div>
              </div>
              <p className="text-lg text-slate-700 mb-4 italic">
                We replace vague "values" with specific engineering specifications.
              </p>
              <ul className="space-y-3 text-lg text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Risk Mapping:</strong> Identify specific failure modes (e.g., "The Digital Mirror" effect where models replicate historical redlining).
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Define Invariants:</strong> Establish non-negotiable boundaries (e.g., "The model shall never infer creditworthiness from zip code").
                  </div>
                </li>
              </ul>
            </div>

            {/* Phase 2 */}
            <div className="mb-10 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-16 h-16 bg-red-800 text-white flex items-center justify-center rounded-lg">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">PHASE 2: THE CONSTITUTION</h3>
                  <p className="text-lg text-red-800 font-semibold">(Normative Design)</p>
                </div>
              </div>
              <p className="text-lg text-slate-700 mb-4 italic">
                We replace implicit human preference with explicit written law.
              </p>
              <ul className="space-y-3 text-lg text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Drafting the Rules:</strong> Create a machine-readable document containing prioritized principles (The Constitution).
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Hierarchy of Rights:</strong> Explicitly code which rules override others (e.g., Safety &gt; Helpfulness). This resolves the "Consistency Paradox" where models get confused by conflicting user instructions.
                  </div>
                </li>
              </ul>
            </div>

            {/* Phase 3 */}
            <div className="mb-10 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-16 h-16 bg-red-800 text-white flex items-center justify-center rounded-lg">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">PHASE 3: THE ENGINE</h3>
                  <p className="text-lg text-red-800 font-semibold">(Technical Implementation)</p>
                </div>
              </div>
              <p className="text-lg text-slate-700 mb-4 italic">
                We automate safety using the "Dual-Model" architecture.
              </p>
              <ul className="space-y-3 text-lg text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>The Critique-and-Revise Loop:</strong> The model generates a draft, critiques it against the Constitution, and revises it before the user sees it.
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Scaled Supervision (RLAIF):</strong> We use the AI to rate its own outputs against the Constitution, removing the bottleneck and subjectivity of human labelers.
                  </div>
                </li>
              </ul>
            </div>

            {/* Phase 4 */}
            <div className="mb-10 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-16 h-16 bg-red-800 text-white flex items-center justify-center rounded-lg">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">PHASE 4: THE AUDIT</h3>
                  <p className="text-lg text-red-800 font-semibold">(Verification &amp; Discovery)</p>
                </div>
              </div>
              <p className="text-lg text-slate-700 mb-4 italic">
                We move from "vibes-based" safety to "metrics-based" engineering.
              </p>
              <ul className="space-y-3 text-lg text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>The Reasoning Trace:</strong> The model produces a step-by-step logic log explaining why it made a decision, citing specific Constitutional clauses.
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Constitutional Error Rate (CER):</strong> We measure exactly how often the model violates a specific clause, providing a hard metric for regulators.
                  </div>
                </li>
              </ul>
            </div>

            {/* Phase 5 */}
            <div className="mb-10 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-16 h-16 bg-red-800 text-white flex items-center justify-center rounded-lg">
                  <span className="text-2xl font-bold">5</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">PHASE 5: THE CULTURE</h3>
                  <p className="text-lg text-red-800 font-semibold">(Operational Readiness)</p>
                </div>
              </div>
              <p className="text-lg text-slate-700 mb-4 italic">
                Safety becomes an asset, not a cost center.
              </p>
              <ul className="space-y-3 text-lg text-slate-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>Incident Response:</strong> When a failure occurs, we do not "patch" the code; we "amend" the Constitution, propagating the fix globally.
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-red-800 mr-3 mt-1 flex-shrink-0"></i>
                  <div>
                    <strong>NIST Alignment:</strong> This phase maps directly to the MANAGE function of the <a href="/cai-engineering-standard" className="text-red-800 hover:text-red-900 underline cursor-pointer">NIST AI Risk Management Framework</a>.
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* The Evidence */}
          <section className="mb-16 bg-red-50 p-8 rounded-lg border-l-4 border-red-800">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">THE EVIDENCE: WHY ENGINEERING WORKS</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Recent benchmarks confirm that Constitutional models structurally outperform standard models:
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl font-bold text-red-800 mb-2">+22%</div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Safety Refusal</div>
                <div className="text-sm text-slate-600">Improved from 72% to 94% against harmful prompts</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl font-bold text-red-800 mb-2">&lt;1.5%</div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Bias Reduction</div>
                <div className="text-sm text-slate-600">Hiring bias against employment gaps reduced from 14%</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-4xl font-bold text-red-800 mb-2">-66%</div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Toxicity</div>
                <div className="text-sm text-slate-600">Toxic outputs reduced by two-thirds</div>
              </div>
            </div>

            <div className="bg-slate-900 text-white p-6 rounded-lg mt-8">
              <p className="text-xl font-bold text-center">
                Conclusion: We do not have to choose between innovation and safety. By adopting these standards, California can lead the world in High-Trust, Legally Defensible AI.
              </p>
            </div>
          </section>

          {/* Related Resources */}
          <section className="mb-16 bg-slate-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Related Resources</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="/book-proposal" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">📚 Full Book Proposal</h4>
                <p className="text-sm text-slate-600">Detailed 17-chapter outline and academic proposal</p>
              </a>
              <a href="/constitutional-ai" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">⚖️ Constitutional AI &amp; Legal Accountability</h4>
                <p className="text-sm text-slate-600">Framework for auditable safety and product liability law</p>
              </a>
              <a href="/cai-engineering-standard" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">🔧 CAI Engineering Standard</h4>
                <p className="text-sm text-slate-600">Bridging technical alignment and risk engineering</p>
              </a>
              <a href="/open-letter-governor-newsom" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">📝 Open Letter to Governor Newsom</h4>
                <p className="text-sm text-slate-600">Policy recommendations for California AI leadership</p>
              </a>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mt-16 p-8 bg-slate-100 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Ready to Implement Constitutional AI?</h3>
            <p className="text-lg text-slate-700 mb-6">
              Contact us to discuss how this framework can transform your AI systems from black boxes to glass boxes.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </a>
          </section>
        </article>

        <Footer />
      </div>
    </>
  );
}